'use client';

import { isSubject, Subject } from '@schuelerhilfe/shared/src/enums/subject';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { BookAppointmentModalResolved } from './types';
import { Button } from '@/components/Button';
import { ContactForm } from '@/components/form/ContactForm';
import { Modal } from '@/components/Modal';
import { submitForm } from '@/lib/api';
import { generateDefaultSlots, loadAppointmentSlots } from '@/lib/appointments';
import { toFieldTextOverrides } from '@/lib/form/fieldTexts';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { ContactFormSubmitData } from '@/lib/form/types';
import { GaEventCategory } from '@/lib/gtm/gaEventCategory';
import { GaFormStep } from '@/lib/gtm/gaFormStep';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { pushGaEvent } from '@/lib/gtm/pushGaEvent';
import { resolvePhonePair } from '@/lib/render/phonePair';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import {
  Step,
  SlotsStatus,
  bookingReducer,
  initialBookingState,
} from '@/src/widgets/book-appointment/bookingReducer';
import { DayPicker } from '@/src/widgets/book-appointment/DayPicker';
import { TimePicker } from '@/src/widgets/book-appointment/TimePicker';

export function BookAppointmentModalWidgetC({
  resolved,
}: {
  resolved: BookAppointmentModalResolved;
}) {
  const { entities, substitutePlaceholders: sub } = useRenderContext();
  // CMS text can carry `{location.*}` tokens — the Matelso phone label above is
  // the common one. The i18n fallbacks never do, so only the editor's value is
  // substituted; an empty CMS string still falls through to the fallback.
  const subText = (value: string | undefined): string | undefined =>
    value ? sub(value) : undefined;
  const loc = entities.location;
  const locationSlug = loc?.slug;
  const tForm = useTranslations('form');
  const tBooking = useTranslations('booking');
  const tCommon = useTranslations('common');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [modalStep, setModalStep] = useState<'form' | 'booking'>('form');
  const formDataRef = useRef<ContactFormSubmitData | null>(null);

  // The SH API Gateway's /api/form/desiredAppointment rejects an empty
  // salutation with a 400, and "Keine Angabe" maps to exactly that, so an
  // appointment offers Herr/Frau only (the contact and voucher forms, whose
  // gateway endpoints accept it, keep the third choice).
  const salutationOptions = [
    { value: 'mr', label: tForm('mr') },
    { value: 'mrs', label: tForm('mrs') },
  ];

  // Values come from the shared enum so the token the backend receives cannot
  // drift from the one its DTO accepts (SH-410); the labels stay translated.
  const subjectOptions = [
    { value: Subject.Mathe, label: tForm('subjectMathe') },
    { value: Subject.Deutsch, label: tForm('subjectDeutsch') },
    { value: Subject.Englisch, label: tForm('subjectEnglisch') },
    { value: Subject.Andere, label: tForm('subjectAndere') },
  ];

  const [state, dispatch] = useReducer(
    bookingReducer,
    locationSlug,
    initialBookingState,
  );
  const { slots, slotsStatus, step, selectedDay, selectedTime, selectedMode } =
    state;

  const activeDays = useMemo(
    () =>
      slotsStatus === SlotsStatus.Fallback && locationSlug
        ? generateDefaultSlots()
        : slots,
    [slotsStatus, locationSlug, slots],
  );

  useEffect(() => {
    if (!locationSlug) {
      return;
    }
    loadAppointmentSlots(locationSlug).then((days) => {
      dispatch(
        days
          ? { type: 'SLOTS_LOADED', slots: days }
          : { type: 'SLOTS_FALLBACK' },
      );
    });
  }, [locationSlug]);

  const selectedDayData = activeDays.find((d) => d.dateIso === selectedDay);

  const handleOpenModal = () => {
    pushCmsEventTracking(resolved.primaryCtaTracking, {
      label: primaryCtaLabel,
    });
    setModalStep('form');
    setModalOpen(true);
  };

  const handleFormValid = (data: ContactFormSubmitData): Promise<void> => {
    formDataRef.current = data;
    dispatch({ type: 'STEP_SET', step: Step.Day });
    setModalStep('booking');
    return Promise.resolve();
  };

  const handleFinalSubmit = async () => {
    const formData = formDataRef.current;
    if (!formData || !selectedDay || !selectedTime) {
      return;
    }

    try {
      await submitForm({
        type: 'appointment',
        salutation: formData.salutation,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone,
        ...(formData.subject && isSubject(formData.subject)
          ? { subject: formData.subject }
          : {}),
        desiredDate: `${selectedDay} ${selectedTime}`.trim(),
        locationSlug: locationSlug ?? '',
        sourceUrl: sourceUrlWithCampaign(),
        customerNumber: formData.customerNumber,
      });
      if (resolved.tracking?.length) {
        pushCmsEventTracking(resolved.tracking);
      }
    } catch {
      // always show success
    }
    if (resolved.submitTracking?.length) {
      pushCmsEventTracking(resolved.submitTracking);
    }
    setModalOpen(false);
    setSubmitted(true);
  };

  // 70.07: the widget's own pair, else the page shell's resolved pair (the
  // Standort's number, or the Domain Setting's when it has none). Previously
  // the number and the label fell back independently, so a widget-configured
  // number could be labelled with the Standort's Matelso text — a label naming
  // a different number from the one dialled — and a Standort without a phone
  // showed no call CTA at all.
  const { phone: phoneNumber, matelsoPhoneLabel } = resolvePhonePair(
    {
      phone: subText(resolved.phone),
      matelsoPhoneLabel: subText(resolved.matelsoPhoneLabel),
    },
    {
      phone: loc?.phone,
      matelsoPhoneLabel: loc?.matelsoPhoneLabel as string | undefined,
    },
    entities.domain,
  );
  const phoneLabel = matelsoPhoneLabel ?? phoneNumber;
  // Hoisted out of the JSX: `handleOpenModal` passes it as the tracking
  // `{label}`, so the event carries the text the visitor actually clicked.
  const primaryCtaLabel =
    subText(resolved.primaryCtaLabel) || tBooking('primaryCtaFallback');

  if (submitted) {
    return (
      <div className="py-8 text-center">
        {resolved.confirmationBody && resolved.confirmationBody.length > 0 ? (
          <RichText nodes={resolved.confirmationBody} />
        ) : (
          <p>{tForm('submissionSuccess')}</p>
        )}
      </div>
    );
  }

  return (
    // Outer box carries the inter-widget rhythm (`pt-4`, top only — see the
    // spacing convention in CLAUDE.md); `lg:pt-0` because from `lg` the panel
    // is visually attached to the hero card above it. The inner box keeps the
    // panel's own `lg:px-4 lg:py-6` chrome, which a `pt-*` on the same element
    // would override.
    <div className="pt-4 lg:pt-0">
      <div className="flex flex-col gap-3 lg:rounded-b-2xl lg:bg-[var(--color-secondary-lighter)] lg:px-4 lg:py-6">
        <Button size="md" onClick={handleOpenModal} className="w-full">
          {primaryCtaLabel}
        </Button>
        {phoneNumber && (
          <Button
            href={`tel:${phoneNumber.replace(/[^+\d]/g, '')}`}
            variant="secondary"
            size="md"
            className="w-full"
            onClick={() =>
              pushCmsEventTracking(resolved.phoneTracking, {
                label: phoneLabel,
                phone: phoneNumber,
              })
            }
          >
            {phoneLabel}
          </Button>
        )}

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          ariaLabel={tBooking('modalAriaLabelC')}
          variant="brand"
        >
          {modalStep === 'form' && (
            <div>
              <div className="mb-6 space-y-2">
                <p className="text-primary text-base font-bold uppercase">
                  {resolved.headline
                    ? sub(resolved.headline)
                        .replace('{step}', '1')
                        .replace('{totalSteps}', '2')
                    : tBooking('headlineFallbackC', {
                        step: 1,
                        totalSteps: 2,
                      })}
                </p>
                <h2 className="text-foreground text-2xl font-medium">
                  {resolved.subheadline
                    ? sub(resolved.subheadline)
                    : tBooking('subheadlineFallbackC')}
                </h2>
                {resolved.description && (
                  <p className="text-foreground text-base">
                    {sub(resolved.description)}
                  </p>
                )}
              </div>

              <ContactForm
                onSubmit={handleFormValid}
                compact
                salutationOptions={salutationOptions}
                subjectOptions={subjectOptions}
                fieldTexts={toFieldTextOverrides(resolved.formTexts)}
                className="[&_input]:bg-card-bg [&_select]:bg-card-bg [&_input]:h-[35px] [&_input]:rounded-full [&_input]:border-0 [&_input]:px-4 [&_input]:text-xs [&_select]:h-[35px] [&_select]:rounded-full [&_select]:border-0 [&_select]:px-4 [&_select]:text-xs"
              >
                {({ isSubmitting }) => (
                  <div className="space-y-3">
                    {resolved.contactFormHint && (
                      <p className="text-muted text-xs font-semibold">
                        <span className="mr-1 text-sm">ⓘ</span>
                        {sub(resolved.contactFormHint)}
                      </p>
                    )}
                    <Button
                      size="md"
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full"
                    >
                      {subText(resolved.contactFormCtaLabel) ||
                        tBooking('contactFormCtaFallback')}
                    </Button>
                    {phoneNumber && (
                      <Button
                        href={`tel:${phoneNumber.replace(/[^+\d]/g, '')}`}
                        variant="secondary"
                        size="md"
                        className="w-full"
                        // Its own field, not `phoneTracking`: calling instead
                        // of opening the dialog and calling halfway through
                        // the form are different points in the funnel.
                        onClick={() =>
                          pushCmsEventTracking(resolved.modalPhoneTracking, {
                            label: phoneLabel,
                            phone: phoneNumber,
                          })
                        }
                      >
                        {phoneLabel}
                      </Button>
                    )}
                  </div>
                )}
              </ContactForm>
            </div>
          )}

          {modalStep === 'booking' && (
            <>
              {slotsStatus === SlotsStatus.Loading && (
                <div className="flex justify-center py-16">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                </div>
              )}

              {activeDays.length > 0 && step === Step.Day && (
                <DayPicker
                  availableDays={activeDays}
                  selectedDay={selectedDay}
                  onSelect={(dateIso) =>
                    dispatch({ type: 'DAY_SELECTED', dateIso })
                  }
                  onNext={() => {
                    if (resolved.dayPickerTracking?.length) {
                      pushCmsEventTracking(resolved.dayPickerTracking);
                    } else {
                      pushGaEvent({
                        event_category: GaEventCategory.FormSubmissionTermin,
                        form_step: GaFormStep.SelectTime,
                      });
                    }
                    dispatch({ type: 'STEP_SET', step: Step.Time });
                  }}
                  ctaLabel={
                    subText(resolved.dayPickerCtaLabel) ||
                    tBooking('dayPickerCta')
                  }
                  heading={subText(resolved.pickerTexts?.dayPickerHeading)}
                  stackButtons
                />
              )}

              {step === Step.Time && selectedDayData && (
                <TimePicker
                  day={selectedDayData}
                  selectedTime={selectedTime}
                  selectedMode={selectedMode}
                  onSelect={({ time, mode }) =>
                    dispatch({ type: 'TIME_SELECTED', time, mode })
                  }
                  onBack={() => dispatch({ type: 'STEP_SET', step: Step.Day })}
                  onNext={() => {
                    if (resolved.timePickerTracking?.length) {
                      pushCmsEventTracking(resolved.timePickerTracking);
                    } else {
                      pushGaEvent({
                        event_category: GaEventCategory.FormSubmissionTermin,
                        form_step: GaFormStep.TimeSelected,
                      });
                    }
                    handleFinalSubmit();
                  }}
                  ctaLabel={
                    subText(resolved.timePickerCtaLabel) ||
                    tBooking('timePickerCta')
                  }
                  backLabel={
                    subText(resolved.timePickerBackLabel) ||
                    tBooking('timePickerBack')
                  }
                  heading={subText(resolved.pickerTexts?.timePickerHeading)}
                  locationGroupLabel={subText(
                    resolved.pickerTexts?.timeSlotLocationLabel,
                  )}
                  phoneGroupLabel={subText(
                    resolved.pickerTexts?.timeSlotPhoneLabel,
                  )}
                  stackButtons
                />
              )}

              {activeDays.length === 0 &&
                slotsStatus !== SlotsStatus.Loading && (
                  <div className="py-8 text-center">
                    <p className="text-foreground">
                      {subText(resolved.pickerTexts?.noSlotsText) ||
                        tBooking('noSlotsAvailable')}
                    </p>
                    <Button
                      size="md"
                      variant="secondary"
                      onClick={() => setModalOpen(false)}
                      className="mt-4"
                    >
                      {tCommon('close')}
                    </Button>
                  </div>
                )}
            </>
          )}

          {resolved.privacyText && resolved.privacyText.length > 0 ? (
            <div className="text-muted [&_a]:text-primary mt-4 text-center text-xs [&_a]:underline">
              <RichText nodes={resolved.privacyText} />
            </div>
          ) : (
            entities.privacyPolicyUrl && (
              <p className="text-muted mt-4 text-center text-xs">
                {tBooking.rich('privacyNotice', {
                  websiteLink: (chunks) => (
                    <a
                      href={entities.privacyPolicyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      {chunks}
                    </a>
                  ),
                  locationLink: (chunks) => (
                    <a
                      href={entities.privacyPolicyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      {chunks}
                    </a>
                  ),
                })}
              </p>
            )
          )}
        </Modal>
      </div>
    </div>
  );
}
